/**
 * AssistQueryService -- lane 7c (J2 contract fix 2026-07-03).
 *
 * The anti-corruption layer between the chatbot query-panel view models and the REAL
 * /api/assist/query wire contract (AssistQueryController):
 *   - request rides `actionKind` at the top level (a `kind` field never binds -> 400);
 *   - responses are `{ actionKind, data, error }` envelopes, where data is the raw
 *     upstream body: WHO_CAN_QUERY -> string[] roster, EXPLAIN_DECISION -> the
 *     HybridAuthorizationDecisionEnvelope (ADR-0102).
 * The EXPLAIN gate trace is assembled from the envelope's ReBAC lens — the lens carries
 * the same GateResult[]/approvalChain/stepUpChallenge shapes tb-gate-trace renders; the
 * composed verdict + deterministic `why` ride alongside. No values are invented here.
 *
 * SECURITY:
 *   - Read-only by contract (lane-7b AssistToolCatalog isNavKind=true).
 *   - No money movement, no entitlement mutation.
 *   - Auth is BFF session cookie; no token exposed to the browser (ADR-0003).
 *
 * MOCK MODE: ASSIST_QUERY_USE_MOCK=true returns fixtures with simulated delay.
 */
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, of, delay, map } from 'rxjs';
import {
  ApiService,
  AssistQueryEnvelope,
  AssistQueryRequest,
  HybridEvaluateResponse,
  WhoCanQueryResponse,
  ExplainDecisionQueryResponse,
} from '../api.service';
import type { ResolveResponse } from '@toybank/ui';

/** Injection token to activate mock mode. */
export const ASSIST_QUERY_USE_MOCK = new InjectionToken<boolean>('ASSIST_QUERY_USE_MOCK', {
  providedIn: 'root',
  factory: () => false,
});

// ---- Mock fixtures -------------------------------------------------------

const MOCK_WHO_CAN: WhoCanQueryResponse = {
  kind: 'WHO_CAN_QUERY',
  orgId: 'org-frostvale',
  actionKind: 'WIRE_INIT',
  roster: ['opus.penguin', 'terra.bloom', 'glacier.wade'],
};

const MOCK_EXPLAIN_TRACE: ResolveResponse = {
  decision: 'ALLOW',
  requiresStepUp: false,
  stepUpChallenge: null,
  gates: [
    { gate: 'ROLE', check: 'User has PAYMENTS_MAKER role', decision: 'ALLOW', detail: '' },
    { gate: 'ENTITLEMENT', check: 'WIRE_INIT grant present', decision: 'ALLOW', detail: 'limit $500,000' },
    { gate: 'LIMIT', check: 'Per-payment limit', decision: 'ALLOW', detail: '$107,500 <= $500,000' },
    { gate: 'MAKER_CHECKER', check: 'Dual control', decision: 'REQUIRES_APPROVAL', detail: 'Amount above $25,000 threshold' },
  ],
  approvalChain: [
    {
      step: 1,
      candidates: [
        { sub: 'terra.bloom', name: 'Terra Bloom', role: 'PAYMENTS_CHECKER' },
        { sub: 'glacier.wade', name: 'Glacier Wade', role: 'PAYMENTS_CHECKER' },
      ],
      requiredCount: 1,
      candidateClass: null,
    },
  ],
  paymentTypeDenied: null,
  validityDenied: null,
  beneficiaryDenied: null,
};

const MOCK_EXPLAIN: ExplainDecisionQueryResponse = {
  kind: 'EXPLAIN_DECISION',
  orgId: 'org-frostvale',
  subjectId: 'opus.penguin',
  actionKind: 'WIRE_INIT',
  gateTrace: MOCK_EXPLAIN_TRACE,
  finalEffect: 'PENDING_APPROVAL',
  why: 'Dual control applies above $25,000 — one PAYMENTS_CHECKER approval is required.',
};

// ---- Envelope adapters ----------------------------------------------------

/** Surface the envelope's error as a stream error so consumers' error paths engage. */
function requireData(env: AssistQueryEnvelope): unknown {
  if (env.error) throw new Error(env.error);
  return env.data;
}

/**
 * Assemble the tb-gate-trace view from the hybrid envelope's ReBAC lens. Every value is
 * server-produced: the lens carries rule-walker GateResult[]/approvalChain/stepUpChallenge
 * verbatim; the 3-value decision reflects the lens's own effect/requiresApproval flags.
 */
function toGateTrace(envelope: HybridEvaluateResponse): ResolveResponse {
  const rebac = envelope.rebac;
  return {
    decision: rebac.requiresApproval ? 'REQUIRES_APPROVAL' : rebac.effect === 'PERMIT' ? 'ALLOW' : 'DENY',
    requiresStepUp: rebac.requiresStepUp,
    stepUpChallenge: rebac.stepUpChallenge,
    gates: rebac.gates,
    approvalChain: rebac.approvalChain,
    paymentTypeDenied: null,
    validityDenied: null,
    beneficiaryDenied: null,
  };
}

// ---- Service -------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class AssistQueryService {
  private readonly api = inject(ApiService);
  private readonly useMock = inject(ASSIST_QUERY_USE_MOCK);

  /**
   * WHO_CAN_QUERY: who in the org can perform the given action.
   * Returns names-only roster (no limits/caps -- those require admin).
   */
  whoCanQuery(orgId: string, actionKind: string, opts?: {
    accountId?: string | null;
    asOfDate?: string | null;
  }): Observable<WhoCanQueryResponse> {
    if (this.useMock) {
      return of({ ...MOCK_WHO_CAN, orgId, actionKind }).pipe(delay(350));
    }
    const req: AssistQueryRequest = {
      actionKind: 'WHO_CAN_QUERY',
      params: { orgId, actionKind, accountId: opts?.accountId, asOfDate: opts?.asOfDate },
    };
    return this.api.assistQuery(req).pipe(
      map((env) => ({
        kind: 'WHO_CAN_QUERY' as const,
        orgId,
        actionKind,
        roster: (requireData(env) as string[] | null) ?? [],
      })),
    );
  }

  /**
   * EXPLAIN_DECISION: why is the given action allowed/denied for a subject.
   * Returns the ReBAC gate trace for tb-gate-trace rendering + the composed verdict
   * and deterministic why from the hybrid envelope.
   * Money amounts are decimal strings (DESIGN.md 7.4).
   */
  explainDecision(opts: {
    orgId: string;
    subjectId: string;
    actionKind: string;
    accountId?: string | null;
    amount?: string | null;
    currency?: string | null;
  }): Observable<ExplainDecisionQueryResponse> {
    if (this.useMock) {
      return of({ ...MOCK_EXPLAIN, orgId: opts.orgId, subjectId: opts.subjectId, actionKind: opts.actionKind }).pipe(delay(450));
    }
    const req: AssistQueryRequest = {
      actionKind: 'EXPLAIN_DECISION',
      params: {
        orgId: opts.orgId,
        actionKind: opts.actionKind,
        subjectId: opts.subjectId,
        accountId: opts.accountId,
        amount: opts.amount,
        currency: opts.currency,
      },
    };
    return this.api.assistQuery(req).pipe(
      map((env) => {
        const envelope = requireData(env) as HybridEvaluateResponse;
        return {
          kind: 'EXPLAIN_DECISION' as const,
          orgId: opts.orgId,
          subjectId: opts.subjectId,
          actionKind: opts.actionKind,
          gateTrace: toGateTrace(envelope),
          finalEffect: envelope.finalEffect,
          why: envelope.why,
        };
      }),
    );
  }
}
